import Cookies from 'js-cookie'
import { clearCachedNeedPlan } from './subscriptionNeedPlanStorage'
import { clearAssignedInstitutesCache } from './assignedInstitutesCache'

const INSTITUTE_ID_KEY = 'institute_id'
const COOKIE_OPTS = { expires: 7, path: '/', sameSite: 'Lax' }

/** Paths that may have been used when cookies were set (CRA routes). */
function getCandidateCookiePaths() {
  const paths = new Set(['/'])
  const segments = window.location.pathname.split('/').filter(Boolean)
  let built = ''
  for (const segment of segments) {
    built += `/${segment}`
    paths.add(built)
  }
  return [...paths]
}

/** Expire a cookie via document.cookie (works when js-cookie remove misses path/sameSite). */
function expireCookieViaDocument(name, path = '/') {
  const encoded = encodeURIComponent(name)
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT'
  const base = [
    `${encoded}=; expires=${expires}; path=${path}`,
    `${encoded}=; Max-Age=0; path=${path}`,
    `${encoded}=; expires=${expires}; path=${path}; SameSite=Lax`,
    `${encoded}=; expires=${expires}; path=${path}; SameSite=Strict`,
  ]
  const host = window.location.hostname
  if (host && host !== 'localhost' && !/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    base.push(`${encoded}=; expires=${expires}; path=${path}; domain=${host}`)
    base.push(`${encoded}=; expires=${expires}; path=${path}; domain=.${host}`)
  }
  base.forEach((entry) => {
    document.cookie = entry
  })
}

/** Remove one cookie across likely path / sameSite variants. */
export function removeCookieEverywhere(name) {
  const paths = getCandidateCookiePaths()
  const sameSiteVariants = [undefined, 'Lax', 'Strict']
  for (const path of paths) {
    expireCookieViaDocument(name, path)
    for (const sameSite of sameSiteVariants) {
      const opts = sameSite ? { path, sameSite } : { path }
      Cookies.remove(name, opts)
      Cookies.remove(name, path === '/' ? {} : opts)
    }
  }
  Cookies.remove(name)
}

/**
 * Returns a positive numeric institute id from cookies, or null if missing / invalid / <= 0.
 */
export function getValidInstituteIdFromCookie() {
  const raw = Cookies.get(INSTITUTE_ID_KEY)
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return null
  }
  const n = Number(String(raw).trim())
  if (!Number.isFinite(n) || n <= 0) {
    return null
  }
  return n
}

export function hasValidInstituteIdCookie() {
  return getValidInstituteIdFromCookie() !== null
}

/**
 * Positive institute id from cookie, else from `user` cookie JSON (login payload).
 */
export function resolveInstituteId() {
  const fromCookie = getValidInstituteIdFromCookie()
  if (fromCookie != null) return fromCookie
  try {
    const userRaw = Cookies.get('user')
    if (!userRaw) return null
    const u = JSON.parse(userRaw)
    const n = Number(u?.institute_id)
    if (Number.isFinite(n) && n > 0) return n
  } catch {
    /* ignore */
  }
  return null
}

/** Axios headers for institute-scoped APIs. Omits header when id is missing/invalid. */
export function resolveInstituteIdHeaders() {
  const id = resolveInstituteId()
  if (id == null) return {}
  return { 'institute-Id': id }
}

/** Default placeholder when institute is missing or invalid (admin flow only). */
export function setDefaultInvalidInstituteIdCookie() {
  Cookies.set(INSTITUTE_ID_KEY, '0', COOKIE_OPTS)
}

/** Remove `institute_id` regardless of path / sameSite used when it was set. */
export function removeInstituteIdCookie() {
  removeCookieEverywhere(INSTITUTE_ID_KEY)
}

const BRANDING_COOKIE_KEYS = [
  'name',
  'institute_name',
  'institute_logo',
  'stamp_url_img',
  'institute_stamp',
  'address',
  'pincode',
  'academic_session_id',
  'session_name',
  'institute_type',
]

/** Institute branding / session metadata cookies (not auth identity). */
export function clearInstituteBrandingCookies() {
  removeInstituteIdCookie()
  BRANDING_COOKIE_KEYS.forEach(removeCookieEverywhere)
  sessionStorage.removeItem('institute_confirmed')
  sessionStorage.removeItem('institute_confirmed_timestamp')
}

const AUTH_COOKIE_KEYS = ['user', 'user_type', 'roles']

/** Full sign-out cookie cleanup (matches AuthContext.logout). */
export function clearAuthCookies() {
  clearCachedNeedPlan()
  clearAssignedInstitutesCache()
  clearInstituteBrandingCookies()
  AUTH_COOKIE_KEYS.forEach(removeCookieEverywhere)
}
